<template>
  <div class="user-page" :key="$route.fullPath">
    <!-- 全屏页面自定义左上角返回按钮 -->
    <div class="page-back-btn" @click="$router.go(-1)">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2">
        <path d="M19 12H5M12 19l-7-7 7-7"></path>
      </svg>
    </div>
    <!-- 顶部用户信息栏 -->
    <div class="user-header" style="padding-left: 50px;">
      <div class="avatar-wrap" @click="triggerUpload">
        <img
          :src="avatarSrc"
          class="avatar-img"
          alt="头像"
        />
        <div class="avatar-mask">更换头像</div>
        <input
          ref="avatarInput"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style="display:none"
          @change="onFileChange"
        />
      </div>
      <div class="name-area">
        <span class="nick">{{ userInfo?.Nickname || "游客" }}</span>
        <button class="edit-name-btn" @click="showNameDialog = true">修改昵称</button>
      </div>
    </div>

    <!-- 整月日历模块 -->
    <div class="calendar-card">
      <div class="calendar-head">
        <span class="calendar-title">{{ currentYear }}年{{ currentMonth }}月</span>
        <div class="calendar-btn-group">
          <button @click="prevMonth">&lt;</button>
          <button @click="nextMonth">&gt;</button>
          <button class="today-btn" @click="resetToToday">今天</button>
        </div>
      </div>

      <div class="week-row">
        <div class="week-item">一</div>
        <div class="week-item">二</div>
        <div class="week-item">三</div>
        <div class="week-item">四</div>
        <div class="week-item">五</div>
        <div class="week-item">六</div>
        <div class="week-item">日</div>
      </div>

      <div class="day-grid">
        <div
          v-for="item in monthDayList"
          :key="item.key"
          class="day-cell"
          :class="{
            otherMonth: item.isOtherMonth,
            today: item.isToday
          }"
        >
          <div class="day-num">{{ item.day }}</div>
          <div v-if="item.readTime || item.readCount" class="day-data">
            <div class="data-line">{{ item.readTime }}分钟</div>
            <div class="data-line">{{ item.readCount }}篇</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部退出登录按钮 -->
    <div class="logout-wrap">
      <button class="logout-btn" @click="handleLogout">退出登录</button>
    </div>

    <!-- 修改昵称弹窗 -->
    <div v-if="showNameDialog" class="dialog-mask" @click.self="showNameDialog = false">
      <div class="dialog-box" @click.stop>
        <h3>修改昵称</h3>
        <input
          v-model="tempNickname"
          class="name-input"
          placeholder="请输入1~12位昵称"
          maxlength="12"
        />
        <div class="dialog-buttons">
          <button class="btn-cancel" @click="showNameDialog = false">取消</button>
          <button class="btn-save" @click="submitNewName">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import { API_BASE } from "@/config/env";
import { readerFetch } from "@/utils/request";
import dayjs from "dayjs";

export default {
  name: "UserInfo",
  data() {
    return {
      API_BASE,
      userInfo: {
        ID: 0,
        Nickname: "",
        Avatar: "",
        MonthRead: []
      },
      currentYear: dayjs().year(),
      currentMonth: dayjs().month() + 1,
      monthDayList: [],
      readRecordMap: {},
      showNameDialog: false,
      tempNickname: "",
      maxOriginFileSize: 5 * 1024 * 1024
    };
  },
  created() {
    this.loadUserInfo();
  },
  computed: {
    avatarSrc() {
      if (!this.userInfo?.Avatar) {
        return "/default_avatar.png";
      }
      return this.API_BASE + this.userInfo.Avatar;
    }
  },
  activated() {
    this.$nextTick(() => {
      const calendar = document.querySelector('.calendar-card');
      if (calendar) {
        calendar.style.display = 'none';
        setTimeout(() => {
          calendar.style.display = '';
        }, 0);
      }
    })
  },
  methods: {
    buildRecordMap() {
      // 修复：null 转为空数组
      const list = this.userInfo.MonthRead || [];
      this.readRecordMap = {};
      list.forEach(item => {
        const cleanDate = item.date.replace(/‑/g, '-');
        this.readRecordMap[cleanDate] = item;
      })
    },

    async loadUserInfo() {
      const readerId = localStorage.getItem("readerId");
      if (!readerId) return;
      try {
        const res = await readerFetch(`/info?reader_id=${readerId}`);
        this.userInfo = {
          ID: 0,
          Nickname: "",
          Avatar: "",
          MonthRead: [],
          ...res.data
        };
        this.tempNickname = this.userInfo.Nickname;
        this.buildRecordMap();
        this.renderMonthCalendar();
      } catch (err) {
        console.error("用户信息加载失败", err);
        this.userInfo = { ID: 0, Nickname: "", Avatar: "", MonthRead: [] };
        this.readRecordMap = {};
        this.renderMonthCalendar();
      }
    },

    renderMonthCalendar() {
      this.buildRecordMap();
      const list = [];
      const now = dayjs(`${this.currentYear}-${this.currentMonth}-01`);
      const firstWeek = now.day() || 7;
      const prevMonth = now.subtract(1, "month");
      const prevTotalDay = prevMonth.daysInMonth();

      for (let i = firstWeek - 1; i > 0; i--) {
        const dayNum = prevTotalDay - i + 1;
        const fullDateStr = prevMonth.format("YYYY-MM-DD");
        const dateArr = fullDateStr.split("-");
        const targetDateStr = `${dateArr[0]}-${dateArr[1]}-${String(dayNum).padStart(2, "0")}`;
        const record = this.readRecordMap[targetDateStr] || {};
        list.push({
          key: `pre-${dayNum}`,
          day: dayNum,
          isOtherMonth: true,
          isToday: false,
          readTime: Math.floor((record.time || 0)/60),
          readCount: record.count || 0
        });
      }

      const currentTotalDay = now.daysInMonth();
      const todayStr = dayjs().format("YYYY-MM-DD");
      for (let d = 1; d <= currentTotalDay; d++) {
        const dateStr = `${this.currentYear}-${String(this.currentMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const record = this.readRecordMap[dateStr] || {};
        list.push({
          key: `cur-${d}`,
          day: d,
          isOtherMonth: false,
          isToday: dateStr === todayStr,
          readTime: Math.floor((record.time || 0)/60),
          readCount: record.count || 0
        });
      }

      const fillCount = 42 - list.length;
      const nextMonth = now.add(1, "month");
      for (let i = 1; i <= fillCount; i++) {
        const fullDateStr = nextMonth.format("YYYY-MM-DD");
        const dateArr = fullDateStr.split("-");
        const targetDateStr = `${dateArr[0]}-${dateArr[1]}-${String(i).padStart(2, "0")}`;
        const record = this.readRecordMap[targetDateStr] || {};
        list.push({
          key: `next-${i}`,
          day: i,
          isOtherMonth: true,
          isToday: false,
          readTime: record.time || 0,
          readCount: record.count || 0
        });
      }
      this.monthDayList = list;
    },

    prevMonth() {
      const date = dayjs(`${this.currentYear}-${this.currentMonth}-01`).subtract(1, "month");
      this.currentYear = date.year();
      this.currentMonth = date.month() + 1;
      this.renderMonthCalendar();
    },

    nextMonth() {
      const date = dayjs(`${this.currentYear}-${this.currentMonth}-01`).add(1, "month");
      this.currentYear = date.year();
      this.currentMonth = date.month() + 1;
      this.renderMonthCalendar();
    },

    resetToToday() {
      this.currentYear = dayjs().year();
      this.currentMonth = dayjs().month() + 1;
      this.renderMonthCalendar();
    },

    handleLogout() {
      localStorage.removeItem("readerId");
      localStorage.removeItem("readerToken");
      this.$router.push("/login");
    },

    async submitNewName() {
      const newName = this.tempNickname.trim();
      if (!newName) {
        alert("昵称不能为空");
        return;
      }
      const readerId = Number(localStorage.getItem("readerId"));
      const res = await readerFetch("/nickname", {
        method: "PUT",
        body: {
          readerId,
          newName
        }
      });
      if (res.code === 0) {
        alert("昵称修改成功");
        this.showNameDialog = false;
        this.userInfo.Nickname = newName;
      } else {
        alert(res.msg);
      }
    },

    triggerUpload() {
      this.$refs.avatarInput.click();
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > this.maxOriginFileSize) {
        alert("原图不能超过5MB，请更换图片");
        this.$refs.avatarInput.value = "";
        return;
      }
      this.compressAvatar(file, 200).then(blob => {
        this.uploadCompressedAvatar(blob);
      });
      this.$refs.avatarInput.value = "";
    },
    compressAvatar(file, targetSize) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = ev => {
          const img = new Image();
          img.src = ev.target.result;
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = targetSize;
            canvas.height = targetSize;
            const minSide = Math.min(img.width, img.height);
            const cutX = (img.width - minSide) / 2;
            const cutY = (img.height - minSide) / 2;
            ctx.drawImage(img, cutX, cutY, minSide, minSide, 0, 0, targetSize, targetSize);
            canvas.toBlob(blob => resolve(blob), "image/png", 0.8);
          };
        };
      });
    },
    async uploadCompressedAvatar(blob) {
      const readerId = localStorage.getItem("readerId");
      const token = localStorage.getItem("readerToken");
      const formData = new FormData();
      formData.append("readerId", readerId);
      formData.append("avatar", blob, "avatar.png");
      try {
        const res = await fetch(`${API_BASE}/api/reader/upload-avatar`, {
          method: "POST",
          headers: {
            "X-Reader-Token": token || ""
          },
          body: formData
        });
        const data = await res.json();
        if (data.code === 0) {
          this.userInfo.Avatar = data.data.avatarUrl;
        } else {
          alert(data.msg);
        }
      } catch (err) {
        alert("上传失败，请检查后端服务是否正常运行");
        console.error("上传异常：", err);
      }
    }
  }
};
</script>

<style scoped>
.user-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999; /* 盖在全局布局上层 */
  overflow-y: auto; /* 文字超长滚动 */

  padding: 24px 16px;
  padding-bottom: 88px;
  background: #f7f9fc;
  box-sizing: border-box;
}

.user-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.avatar-wrap {
  width: 88px;
  height: 88px;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.avatar-mask {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  text-align: center;
  padding: 4px 0;
  border-bottom-left-radius: 44px;
  border-bottom-right-radius: 44px;
}

.name-area {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nick {
  font-size: 22px;
  font-weight: 500;
  color: #222;
}

.edit-name-btn {
  width: fit-content;
  padding: 4px 12px;
  border: 1px solid #7b61ff;
  color: #7b61ff;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
}
.calendar-card {
  background: #fff;
  border-radius: 12px;
  padding: 12px 8px;
  width: 100%;
  box-sizing: border-box;
  user-select: none;
  -webkit-user-select: none;
}

.calendar-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.calendar-title {
  font-size: 16px;
  font-weight: 500;
}

.calendar-btn-group button {
  padding: 2px 6px;
  margin: 0 2px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
}

.today-btn {
  color: #7b61ff;
  border-color: #7b61ff !important;
}

.week-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}

.week-item {
  text-align: center;
  font-size: 12px;
  color: #666;
  padding: 2px 0;
}

.day-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  /* 手机端降低单元格高度 */
  grid-template-rows: repeat(6, 52px);
  gap: 4px;
  width: 100%;
}

.day-cell {
  min-height: 52px;
  padding: 4px 3px;
  border-radius: 8px;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
}
.day-cell.otherMonth {
  opacity: 0.35;
}
.day-cell.today {
  border-color: #7b61ff;
  background: #f6f4ff;
}

.day-num {
  font-size: 14px;
  line-height: 1;
  align-self: center;
}

.day-data {
  font-size: 9px;
  color: #555;
  text-align: right;
  line-height: 1.2;
}
.data-line {
  white-space: nowrap;
}

.logout-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 24px;
  padding: 0 16px;
  z-index: 10;
  display: flex;
  justify-content: center;
}

.logout-btn {
  width: 320px;
  padding: 12px 0;
  background: #ff4d4f;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  white-space: nowrap;
}
.logout-btn:active {
  opacity: 0.8;
}

/* 通用弹窗遮罩 */
.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dialog-box {
  background: #fff;
  padding: 24px;
  border-radius: 10px;
  width: 320px;
}
.dialog-box h3 {
  margin-top: 0;
  font-size: 18px;
}
.name-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  margin: 12px 0 20px;
}
.dialog-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
.btn-cancel {
  padding: 6px 16px;
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
}
.btn-save {
  padding: 6px 16px;
  background: #7b61ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
/* 路由切换/页面重新进入时，强制重绘日历布局，解决返回后错乱 */
.calendar-card, .day-grid {
  contain: layout size style;
  will-change: width;
}

/* Safari专属兼容，修复grid自动宽度计算bug */
@supports (-webkit-touch-callout: none) {
  .day-grid {
    display: -webkit-grid;
    width: 100%;
  }
}
</style>